import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import {
  Globe,
  AppWindow,
  ShoppingCart,
  ShieldCheck,
  Users,
  BarChart3,
  Video,
  Clock4,
  Megaphone,
  GraduationCap,
  MessageSquare,
  Calculator,
  Trophy,
  Briefcase,
  Ticket,
  ArrowRight,
  X,
  Brain,
  Link2,
  Bot,
} from 'lucide-react';
import { ProductFlagshipGrid } from "./ProductFlagshipGrid";
import { ProductModulesGrid } from "./ProductModulesGrid";
import BlockchainSpotlight from "./BlockchainSpotlight";
import { navigationToasts } from "@/utils/toastUtils";

const spotlight = {
  name: "Centurion Global Protect",
  tagline: "Enterprise-Grade Protection",
  description: "Comprehensive security suite with advanced threat detection, compliance monitoring, and data protection.",
  icon: ShieldCheck,
  image: "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?auto=format&fit=crop&w=900&q=80",
  href: "/products/centurion-global-protect",
  cta: "Learn More About Centurion",
};

const menuProducts = [
  {
    name: 'All Products',
    icon: Globe,
    description: "See all our products by category and products in development.",
    href: '/products',
    cta: "View All Products"
  },
  {
    name: 'AI Agents',
    icon: Bot,
    description: "Transform your business with intelligent agents. 24/7 automation for customer service, sales, and operations.",
    href: '/ai-agents',
    cta: "Discover AI Agents"
  },
  {
    name: 'Websites & Web Apps',
    icon: AppWindow,
    description: "Enterprise-grade Websites & Web Apps for security, scalability, and ease. Custom built, branded and modular.",
    href: '/products/website-webapp-suite',
    cta: "Explore Websites & Web Apps"
  },
  {
    name: 'Artificial Intelligence',
    icon: Brain,
    description: "Next-generation AI chat, voice, and automation. Enhance experiences with smart, adaptive technology.",
    href: '/ai-agents',
    cta: "Explore AI Solutions"
  },
];

const productCategories = [
  { name: 'Core Applications', icon: Globe, href: '/products?category=Core Applications' },
  { name: 'Intelligence & Analytics', icon: BarChart3, href: '/products?category=Intelligence & Analytics' },
  { name: 'Financial Management', icon: Calculator, href: '/products?category=Financial Management' },
  { name: 'Productivity & Collaboration', icon: Users, href: '/products?category=Productivity & Collaboration' },
  { name: 'Legal & Compliance', icon: ShieldCheck, href: '/products?category=Legal & Compliance' },
  { name: 'Education & Training', icon: GraduationCap, href: '/products?category=Education & Training' },
  { name: 'Marketing & Sales', icon: Megaphone, href: '/marketing/demo' },
  { name: 'Human Resources', icon: Briefcase, href: '/products?category=Human Resources' },
  { name: 'Operations', icon: Clock4, href: '/products?category=Operations' },
  { name: 'Industry Solutions', icon: Trophy, href: '/products?category=Industry Solutions' },
  { name: 'Service & Hospitality', icon: MessageSquare, href: '/products?category=Service & Hospitality' },
  { name: 'Consulting & Advisory', icon: Link2, href: '/products?category=Consulting & Advisory' }
];

interface ProductMegaMenuProps {
  onClose: () => void;
  onNavigationClick?: (href: string) => void;
  onUserActivity?: () => void;
}

export const ProductMegaMenu: React.FC<ProductMegaMenuProps> = ({ onClose, onNavigationClick, onUserActivity }) => {
  const handleLinkClick = (href: string, e: React.MouseEvent) => {
    e.preventDefault();
    
    if (onNavigationClick) {
      onNavigationClick(href);
    }
    onClose();
  };

  const handleProjectWizardClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onNavigationClick) {
      onNavigationClick("/website-wizard");
    }
    onClose();
  };

  const handleUserActivity = () => {
    if (onUserActivity) {
      onUserActivity();
    }
  };

  return (
    <div
      className="relative bg-white shadow-xl border-t border-l border-r border-b border-gray-100 z-[160] w-full max-w-5xl mx-auto overflow-hidden pointer-events-auto"
      style={{
        minWidth: 360
      }}
      tabIndex={0}
      role="dialog"
      aria-label="Products menu"
      onMouseMove={handleUserActivity}
      onClick={handleUserActivity}
      onKeyDown={handleUserActivity}
      onTouchStart={handleUserActivity}
    >
      {/* Close button in top-right */}
      <button
        aria-label="Close Products Menu"
        onClick={onClose}
        className="absolute top-4 right-4 z-10 bg-gray-100/80 hover:bg-gray-200 focus:bg-gray-300 p-1.5 transition-all duration-150 shadow-sm border border-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        tabIndex={0}
        type="button"
      >
        <X className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
      </button>
      <div className="flex flex-col md:flex-row">
        {/* Left spotlight area */}
        <div className="flex-1 px-6 py-4 flex flex-col justify-start items-start bg-gradient-to-b from-white/99 to-gray-50 relative min-w-[320px] gap-2">
          {/* Centurion Global Protect */}
          <div className="w-full">
            <div className="flex items-center mb-3">
              <spotlight.icon className="w-8 h-8 text-gray-900 mr-3" />
              <span className="text-xl font-semibold text-gray-800 tracking-tight">{spotlight.name}</span>
            </div>
            <span className="block text-sm font-medium text-blue-600 mb-2 tracking-wide">{spotlight.tagline}</span>
            <p className="text-sm text-gray-700 mb-3 leading-relaxed max-w-sm">{spotlight.description}</p>
            <button
              onClick={(e) => handleLinkClick(spotlight.href, e)}
              className="text-sm px-6 py-2 shadow-sm bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold transition-all duration-150 rounded inline-flex items-center"
            >
              {spotlight.cta}
              <ArrowRight className="ml-2 w-4 h-4" />
            </button>
            {spotlight.image && (
              <img
                src={spotlight.image}
                alt={spotlight.name}
                className="mt-4 w-full object-cover max-h-28 shadow-xl border border-gray-100"
                style={{ objectFit: 'cover' }}
              />
            )}
          </div>
          {/* Project Wizard */}
          <div className="mt-2 w-full flex">
            <button 
              onClick={handleProjectWizardClick}
              className="text-base px-6 py-3 font-semibold w-full shadow-md rounded bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white border-0 transition-all duration-300 hover:shadow-lg inline-flex items-center justify-center"
            >
              Project Wizard
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
          {/* Blockchain Smart Contracts */}
          <BlockchainSpotlight onNavigationClick={onNavigationClick} />
        </div>
        {/* Flagship & modules */}
        <div className="flex-[2] px-6 py-4 bg-white/90">
          <div className="mb-4">
            <h3 className="text-xl font-semibold text-gray-900 tracking-tight mb-1">Our Flagship Products</h3>
            <p className="text-gray-500 text-sm">Seamless solutions for business growth</p>
          </div>
          <ProductFlagshipGrid products={menuProducts} onClose={onClose} onNavigationClick={onNavigationClick} />
          <div className="mt-3">
            <div className="flex items-center mb-2">
              <span className="text-xs font-semibold text-muted-foreground tracking-widest uppercase mr-2">Categories</span>
              <span className="flex-1 border-t border-dashed border-gray-200" />
            </div>
            <ProductModulesGrid modules={productCategories} onClose={onClose} onNavigationClick={onNavigationClick} />
          </div>
        </div>
      </div>
    </div>
  );
};
